import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { WizardLocation } from "../../../../PermitContextProvider/types";
import { LocationButtonList } from "../../../LocationButtonList";
import { SelectionPromptButton } from "./SelectionPromptButton";
export type DateSelectMode =
	| "user-select-start-and-end-dates"
	| "user-select-start-date-only"
	| "user-select-expiry-date-only";

const DATE_SELECT_PROMPT_LABELS = {
	startAndEndDates: msg`Choose Dates`,
	startDateOnly: msg`Choose Start Date`,
	expiryDateOnly: msg`Choose Expiry Date`,
	times: msg`Choose Times`,
} as const satisfies Record<string, MessageDescriptor>;

const dateSelectPromptLabel = (mode: DateSelectMode | null): MessageDescriptor => {
	switch (mode) {
		case "user-select-start-and-end-dates":
			return DATE_SELECT_PROMPT_LABELS.startAndEndDates;
		case "user-select-start-date-only":
			return DATE_SELECT_PROMPT_LABELS.startDateOnly;
		case "user-select-expiry-date-only":
			return DATE_SELECT_PROMPT_LABELS.expiryDateOnly;
		default:
			return DATE_SELECT_PROMPT_LABELS.times;
	}
};
type ParkingSelectionPromptProps = {
	isSelected: boolean;
	isSelectionPromptHidden?: boolean;
	hasTimesSelected: boolean;
	hasLocationChoice: boolean;
	dateSelectMode: DateSelectMode | null;
	locations: WizardLocation[];
	timeError?: string;
	locationError?: string;
	permitTypeId: number;
	onOpenLocationModal: () => void;
	onOpenTimeModal: () => void;
	isLocationLocked?: boolean;
	isValidatingCapacity?: boolean;
};
export const ParkingSelectionPrompt = ({
	isSelected,
	isSelectionPromptHidden = false,
	hasTimesSelected,
	hasLocationChoice,
	dateSelectMode,
	locations,
	timeError,
	locationError,
	permitTypeId,
	onOpenLocationModal,
	onOpenTimeModal,
	isLocationLocked,
	isValidatingCapacity = false,
}: ParkingSelectionPromptProps) => {
	const { i18n, t } = useLingui();
	if (!isSelected || isSelectionPromptHidden) {
		return null;
	}
	const hasLocationsSelected = locations.length > 0;
	if (!hasTimesSelected && hasLocationChoice && !hasLocationsSelected && !isLocationLocked) {
		return (
			<SelectionPromptButton
				label={t`Choose Times and Locations`}
				onClick={onOpenTimeModal}
				hasError={!!timeError && !!locationError}
				errorMessage={t`Time and location choices are required`}
				errorId={`${permitTypeId}-error`}
				isDisabled={isValidatingCapacity}
			/>
		);
	}
	return (
		<>
			{!hasTimesSelected && (
				<SelectionPromptButton
					label={i18n._(dateSelectPromptLabel(dateSelectMode))}
					onClick={onOpenTimeModal}
					hasError={!!timeError}
					errorMessage={t`Time selection is required`}
					errorId={`${permitTypeId}-error`}
					isDisabled={isValidatingCapacity}
				/>
			)}

			{hasLocationsSelected ? (
				<div>
					<LocationButtonList
						locations={locations}
						variant="secondary"
						onLocationClick={hasLocationChoice && !isLocationLocked ? onOpenLocationModal : undefined}
						collapsible
					/>
				</div>
			) : (
				hasLocationChoice &&
				!isLocationLocked && (
					<SelectionPromptButton
						label={t`Choose Parking Location`}
						onClick={onOpenLocationModal}
						hasError={!!locationError}
						errorMessage={t`Location Choice Required`}
						errorId={`${permitTypeId}-error`}
						isLoading={isValidatingCapacity}
						isDisabled={isValidatingCapacity}
					/>
				)
			)}
		</>
	);
};
